import {css} from '@emotion/css';
import React, {useCallback, useContext, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, Modal} from '@veneer/core';

import {ButtonLink, useWolfTheme} from '@br/mg-components';

import {BuyOrActivateLicenseContext} from './BuyOrActivateLicenseContext';

const LinkEntry = ({label, url, helpText}: {label: string; url: string; helpText: string}): JSX.Element => {
    return (
        <>
            <div>{helpText}</div>
            <ButtonLink
                className="w-full"
                href={url}
                appearance="secondary"
                small
            >
                {label}
            </ButtonLink>
        </>
    );
};

const BuyOrActivateLicenseModal = ({tenantId}: {tenantId?: string}): JSX.Element => {
    const {t} = useTranslation(['licenses']);
    const theme = useWolfTheme();
    const {
        buyLicenseUrl,
        activateLicenseUrl: baseActivateLicenseUrl,
        showBuyActivateLinks,
    } = useContext(BuyOrActivateLicenseContext);

    const [open, setOpen] = useState(false);

    const doOpen = useCallback(() => setOpen(true), []);
    const doClose = useCallback(() => setOpen(false), []);

    const activateLicenseUrl = useMemo(() => {
        // eslint-disable-next-line i18next/no-literal-string
        return tenantId ? `${baseActivateLicenseUrl}?tenant=${tenantId}` : baseActivateLicenseUrl;
    }, [tenantId, baseActivateLicenseUrl]);

    const buyOrActivate = useMemo(() => t('licenses:buyOrActivate', 'Buy or activate license'), [t]);

    const classNames = useMemo(
        () => ({
            modal: css({
                ['[role="dialog"]']: {
                    width: '600px',
                },
            }),
            links: css({
                display: 'grid',
                gridTemplateColumns: '3fr 2fr',
                gridColumnGap: theme.orbitalFour,
                gridRowGap: theme.orbitalFour,
                alignItems: 'center',
            }),
        }),
        [theme.orbitalFour]
    );

    if (!showBuyActivateLinks) {
        /* More extensive logic can go here to build up the rules of what to show, but as we just have a
           on off right now let's keep it simple*/
        return <></>;
    }

    return (
        <>
            <Button
                appearance="primary"
                small
                onClick={doOpen}
            >
                {buyOrActivate}
            </Button>
            <Modal
                title={buyOrActivate}
                show={open}
                onClose={doClose}
                className={classNames.modal}
                closeButton
                footer={
                    <Button
                        appearance="primary"
                        onClick={doClose}
                    >
                        {t('licenses:done', 'Done')}
                    </Button>
                }
            >
                <div className={classNames.links}>
                    <LinkEntry
                        label={t('licenses:buy', 'Buy')}
                        url={buyLicenseUrl}
                        helpText={t('licenses:buyLicenseHelp', 'Purchase licenses from HP or an HP partner')}
                    />
                    <LinkEntry
                        label={t('licenses:activate', 'Activate')}
                        url={activateLicenseUrl}
                        helpText={t('licenses:activateLicenseHelp', 'Activate existing license')}
                    />
                </div>
            </Modal>
        </>
    );
};

export default BuyOrActivateLicenseModal;
